import type { ParsedUrlQuery } from 'querystring';
import { pick } from 'accept-language-parser';
import i18next, { TFunction } from 'i18next';
import { logRejectionsToSentry } from '../../Sentry';
import enBaseGenerated from '../../i18n/locales/en/base.generated.json';
import { BrowserService } from '../BrowserService';
import { Public } from '../Public.type';
import { SsrData } from '../SsrData';
import { SsrBackend } from './SsrBackend';
import { DEFAULT_LOCALE, NAMESPACES, NS_BASE_GENERATED, supportedLanguages } from './constants';

const supportedLngs: string[] = supportedLanguages.map(lang => lang.code);

export class I18nService {
  constructor(
    private ssrData: SsrData,
    private browser: Public<BrowserService>,
    query?: ParsedUrlQuery,
  ) {
    logRejectionsToSentry('Initialising i18next instance', () => this.i18nextInstance.init());

    if (query?.lang) {
      this.slot.initialLocaleName = pick(supportedLngs, String(query.lang)) ?? DEFAULT_LOCALE;
    }

    this.changeLanguage(this.slot.initialLocaleName);
  }

  private slot: Slot = this.ssrData.slot('I18N_CACHE', () => ({
    initialLocaleName: DEFAULT_LOCALE,
  }));

  private backend = new SsrBackend(this.ssrData);

  public changeLanguage = (language: string) => {
    logRejectionsToSentry('Changing i18next language during service construction', () =>
      this.i18nextInstance.changeLanguage(language, err => {
        if (err) return;

        const newLanguage = this.i18nextInstance.language;
        this.slot.initialLocaleName = newLanguage;

        if (this.browser.document?.documentElement) {
          this.browser.document.documentElement.lang = newLanguage;
        }
      }),
    );
  };

  public i18nextInstance = i18next
    .createInstance({
      fallbackLng: DEFAULT_LOCALE,
      keySeparator: false,
      interpolation: { escapeValue: false },
      ns: NAMESPACES,
      defaultNS: NAMESPACES[0],
      fallbackNS: NAMESPACES.slice(1),
      returnEmptyString: false,
      supportedLngs: supportedLngs,
      react: { useSuspense: false },
      partialBundledLanguages: true,
      resources: {
        en: {
          [NS_BASE_GENERATED]: enBaseGenerated,
        },
      },
    })
    .use(this.backend);
}

export const makeTestTFunction = (): TFunction => {
  const ssrData = new SsrData();
  const service = new I18nService(ssrData, new BrowserService(ssrData));
  return service.i18nextInstance.t.bind(service.i18nextInstance);
};

interface Slot {
  initialLocaleName: string;
}
